import { action, autorun, computed, makeObservable, observable, } from 'mobx';
import { FeatureValueStore } from './FeatureValueStore';
import { stripHtml } from '../util/helpers';
import Service from '../util/Service';
import { featuresTypes } from '../defs/featureTypes';
export class ConsentStore {
    constructor({ id, shortInfo, ...restData }) {
        this.channels = undefined;
        this.dataTypes = undefined;
        this.granted = undefined;
        this.productTypes = undefined;
        this.purposeTypes = undefined;
        this.modified = false;
        makeObservable(this, {
            channels: observable,
            dataTypes: observable,
            granted: observable,
            productTypes: observable,
            purposeTypes: observable,
            modified: observable,
            setModified: action,
            setGranted: action,
            features: computed,
            hasGrantedFeature: computed,
            hasNotGrantedFeature: computed,
            hasAllFeaturesUndefined: computed,
            hasModifiedFeatures: computed,
            shortInfo: computed,
            featureTitlesAsString: computed,
            className: computed,
        });
        this.id = id;
        this.$shortInfo = shortInfo;
        Object.assign(this, restData);
        this.service = new Service();
        featuresTypes.forEach((featureName) => {
            var _a;
            if (restData[featureName]) {
                this[featureName] = (_a = restData[featureName]) === null || _a === void 0 ? void 0 : _a.map((f) => new FeatureValueStore(f));
            }
        });
        autorun(() => {
            if (this.features.length > 0 && !this.hasAllFeaturesUndefined) {
                this.setModified(this.hasModifiedFeatures || !!this.modified);
                this.setGranted(this.hasGrantedFeature);
            }
        });
    }
    change(value) {
        if (value !== this.granted) {
            this.setGrantedWithFeatures(value);
            this.setModified(true);
            this.service.fireEvent({
                name: 'consents/CHANGE',
                level: 'info',
                message: 'user changed a consent',
            });
        }
    }
    setGrantedWithFeatures(granted) {
        this.setGranted(granted);
        this.features.forEach((feature) => {
            feature.setGranted(granted);
        });
    }
    setGranted(granted) {
        this.granted = granted;
    }
    setModified(modified) {
        this.modified = modified;
    }
    get features() {
        // we are assuming that there is always only *one* feature attached
        return this.featureType
            ? this[this.featureType]
            : [];
    }
    get featureType() {
        // we are assuming that there is always only *one* feature attached
        return featuresTypes.find((featureName) => !!this[featureName]);
    }
    get hasGrantedFeature() {
        return !!this.features.find((f) => f.granted);
    }
    get hasNotGrantedFeature() {
        return !!this.features.find((f) => !f.granted) && !this.hasAllFeaturesUndefined;
    }
    get hasAllFeaturesUndefined() {
        return !this.features.find((feature) => feature.granted !== undefined);
    }
    get hasModifiedFeatures() {
        return !!this.features.find((f) => f.modified);
    }
    get className() {
        if (this.modified) {
            return 'edited';
        }
        if (this.deprecated) {
            return 'outdated';
        }
        if (this.granted && this.hasNotGrantedFeature) {
            return 'part_agreed';
        }
        if (this.granted) {
            return 'agreed';
        }
        if (this.granted === undefined) {
            return undefined;
        }
        if (!this.granted) {
            return 'rejected';
        }
        return undefined;
    }
    get shortInfo() {
        var _a;
        const re = /(<span class="coms-features-\w+">)(.*)(<\/span>)/;
        return (_a = this.$shortInfo) === null || _a === void 0 ? void 0 : _a.replace(re, this.featureTitlesAsString ? `$1${this.featureTitlesAsString}$3` : '$1$3');
    }
    get featureTitlesAsString() {
        return this.features
            .filter((f) => f.granted && f.title)
            .map((f) => stripHtml(f.title))
            .join(', ');
    }
    toJSON() {
        const ret = {
            ...this,
        };
        featuresTypes.forEach((featureType) => {
            var _a;
            if (this[featureType]) {
                ret[featureType] = (_a = this[featureType]) === null || _a === void 0 ? void 0 : _a.map((feature) => feature.toJSON());
            }
        });
        return ret;
    }
    toSaveConsent() {
        const { id, granted = false, featureType, features, } = this;
        return {
            id,
            granted,
            ...(featureType
                ? { [featureType]: features.map((feature) => feature.toSaveFeatureValue()) }
                : {}),
        };
    }
    toShortConsent() {
        const { id, granted = false, featureType, features, shortInfo = '', version = '', } = this;
        return {
            id,
            granted,
            ...(featureType
                ? { [featureType]: features.map((feature) => feature.toShortFeatureValue()) }
                : {}),
            shortInfo,
            version,
        };
    }
}
//# sourceMappingURL=ConsentStore.js.map